import { useSelector } from 'react-redux';

import insuranceGFA from 'images/brands-unique/gfa/insurance.png';
import insuranceETN from 'images/brands-unique/etn/insuranceETN.png';
import insuranceBrasilia from 'images/brands-unique/brasilia/insuranceBrasilia.png';
import insuranceGontijo from 'images/brands-unique/gontijo/insuranceGontijo.svg';

import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import insuranceShieldIcon from 'utils/insuranceShieldIcon';
import { getInsuranceCoverageByBrand } from '../config/insurance/brands';

const localInsuranceIcons = {
  default: insuranceETN,
  gfa: insuranceGFA,
  brasilia: insuranceBrasilia,
  gontijo: insuranceGontijo,
};

/**
 * Brands whose mark is drawn from the theme rather than shipped as an image.
 *
 * The drawn shield sits between a tenant's configured `insurance.icon` and showing nothing,
 * so a brand listed here gets a mark that follows its palette and still yields to artwork
 * the tenant supplies. A brand absent from this set behaves as it did before.
 */
const brandsWithDrawnIcon = new Set(['rochoa']);

/**
 * The brand's primary colour. Selected as a string rather than through `useWhitelabelTheme`,
 * which also pulls in `useTranslation` and hands back a fresh object on every call — a
 * string is referentially stable, so this adds a subscription without adding re-renders.
 * @param {object} state - Redux state
 * @returns {string|undefined} - Hex colour, when the tenant's theme declares one
 */
const selectPrimaryColor = (state) => state.whitelabelConfig?.env?.theme?.colors?.primary;

/**
 * Title context by brand, used to pick the `Insurance Checkbox Title_<context>` copy.
 * Anything absent falls back to 'coverage', which is what every brand but brasilia resolved to
 * before this was a map.
 */
const titleContextByBrand = {
  brasilia: 'brasilia',
  rochoa: 'rochoa',
};

/**
 * useInsuranceConfig hook.
 * Provide insurance icon, details URL, price and resolved coverage config by brand.
 * @returns {{icon: string, brandIcon: string|null, detailsUrl: string|Array, price: number, coverage: object, disclaimerConfig: string|object, titleContext: string, taxRate: string}}
 */
const useInsuranceConfig = () => {
  const { insurance, brand } = useWhitelabelEnvs();
  const primaryColor = useSelector(selectPrimaryColor);
  const localInsuranceIcon = localInsuranceIcons[brand] ?? localInsuranceIcons.default;
  const coverageConfig = getInsuranceCoverageByBrand(brand, insurance?.coverages || []);

  const titleContext = titleContextByBrand[brand] ?? 'coverage';
  const drawnIcon = brandsWithDrawnIcon.has(brand) ? insuranceShieldIcon(primaryColor) : null;

  return {
    icon: insurance?.icon || localInsuranceIcon,
    // Configured artwork first, then the drawn shield, then nothing. Deliberately not
    // `icon`'s chain: that one ends at the ETN default, which is right for the
    // whole-purchase UI but would put another carrier's mark on a brand that has simply
    // supplied none. A brand outside `brandsWithDrawnIcon` still ends at null.
    brandIcon: insurance?.icon || drawnIcon,
    detailsUrl: insurance?.url || '',
    price: insurance?.price || 0,
    coverage: coverageConfig,
    disclaimerConfig: insurance?.disclaimerText || null,
    titleContext,
    taxRate: insurance?.taxRate || '0',
  };
};

export default useInsuranceConfig;
